// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import store from '../store';
import { getUserProfile } from '@/composables/useFirestore';

import AppHome from '../views/home/AppLanding.vue';
import UserLogin from '../views/account/UserLogin.vue';
import UserRegister from '../views/account/UserRegister.vue';
import UserProfile from '../views/account/UserProfile.vue';
import ForgotPassword from '../views/account/ForgotPassword.vue';
import UserUpdateProfile from '../views/account/UserUpdateProfile.vue';
import UserChangePassword from '../views/account/UserChangePassword.vue';
import UserUpdateProfilePicture from '../views/account/UserUpdateProfilePicture.vue';
import UserAccountDelete from '../views/account/UserAccountDelete.vue';
import UserUpdateUniversity from '../views/account/UserUpdateUniversity.vue';
import NotFound from '@/views/404/NotFound.vue';
import TimetablePage from '../views/timetable/TimetablePage.vue';
import ClassesPage from '../views/timetable/ClassesPage.vue';
import SemesterTestsPage from '../views/timetable/SemesterTestsPage.vue';
import PersonalTimetablePage from '../views/timetable/PersonalTimetablePage.vue';
import AcademicCalendarPage from '../views/timetable/AcademicCalendarPage.vue';
import CreditsTrackerPage from '../views/timetable/CreditsTrackerPage.vue';
import SocialsPage from '../views/socials/SocialsPage.vue';
import ChatPage from '../views/socials/ChatPage.vue';
import UserDetailPage from '../views/socials/UserDetailPage.vue';
import PostsPage from '../views/socials/PostsPage.vue';
import ResourcesPage from '../views/resources/ResourcesPage.vue';
import BusSchedulePage from '../views/bus/BusSchedulePage.vue';
import HustleUpPage from '../views/marketplace/HustleUpPage.vue';
import AdminPage from '../views/admin/AdminPage.vue';

const routes = [
  {
    path: '/',
    // handled in guard to redirect based on auth
    beforeEnter: (to, from, next) => { next(); },
    meta: { title: 'Success Boost' }
  },

  // Public marketing/landing
  { path: '/', component: AppHome, meta: { title: 'Welcome | Success Boost' } },
  { path: '/login', component: UserLogin, meta: { title: 'Sign In | Success Boost' } },
  { path: '/register', component: UserRegister, meta: { title: 'Create Account | Success Boost' } },
  { path: '/forgot-password', component: ForgotPassword, meta: { title: 'Reset Password | Success Boost' } },

  // Protected
  { path: '/profile', component: UserProfile, meta: { requiresAuth: true, title: 'Profile | Success Boost' } },
  { path: '/update-profile', component: UserUpdateProfile, meta: { requiresAuth: true, title: 'Update Profile | Success Boost' } },
  { path: '/update-profile-picture', component: UserUpdateProfilePicture, meta: { requiresAuth: true, title: 'Update Profile Picture | Success Boost' } },
  { path: '/change-password', component: UserChangePassword, meta: { requiresAuth: true, title: 'Change Password | Success Boost' } },
  { path: '/delete-account', component: UserAccountDelete, meta: { requiresAuth: true, title: 'Delete Account | Success Boost' } },
  { path: '/update-university', component: UserUpdateUniversity, meta: { requiresAuth: true, title: 'Update University | Success Boost' } },

  { path: '/timetable', component: TimetablePage, meta: { requiresAuth: true, title: 'Timetable | Success Boost' } },
  { path: '/timetable/classes', component: ClassesPage, meta: { requiresAuth: true, title: 'Classes | Success Boost' } },
  { path: '/timetable/tests', component: SemesterTestsPage, meta: { requiresAuth: true, title: 'Semester Tests | Success Boost' } },
  { path: '/timetable/personal', component: PersonalTimetablePage, meta: { requiresAuth: true, title: 'Personal Schedule | Success Boost' } },
  { path: '/timetable/calendar', component: AcademicCalendarPage, meta: { requiresAuth: true, title: 'Academic Calendar | Success Boost' } },
  { path: '/timetable/credits', component: CreditsTrackerPage, meta: { requiresAuth: true, title: 'Credits Tracker | Success Boost' } },

  { path: '/socials', component: SocialsPage, meta: { requiresAuth: true, title: 'Socials | Success Boost' } },
  { path: '/chat', component: ChatPage, meta: { requiresAuth: true, title: 'Chat | Success Boost' } },
  { path: '/chat/:userId', component: ChatPage, meta: { requiresAuth: true, title: 'Chat | Success Boost' } },
  { path: '/user/:userId', component: UserDetailPage, meta: { requiresAuth: true, title: 'User Profile | Success Boost' } },
  { path: '/posts', component: PostsPage, meta: { requiresAuth: true, title: 'Posts | Success Boost' } },
  { path: '/hustleup', component: HustleUpPage, meta: { requiresAuth: true, title: 'HustleUP | Success Boost' } },

  // Public resources (no auth)
  { path: '/resources', component: ResourcesPage, meta: { title: 'Resources | Success Boost' } },

  // Bus schedule (protected)
  { path: '/bus', component: BusSchedulePage, meta: { requiresAuth: true, title: 'Bus Schedule | Success Boost' } },

  // Admin panel (admin only)
  { path: '/admin', component: AdminPage, meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin Panel | Success Boost' } },

  // 404
  { path: '/:pathMatch(.*)*', component: NotFound, meta: { title: '404 Not Found | Success Boost' } }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { top: 0 };
  }
});

// Wait for Firebase to confirm auth state (fix for refresh issues)
function getCurrentUser() {
  return new Promise((resolve, reject) => {
    const removeListener = onAuthStateChanged(getAuth(), user => {
      removeListener();
      resolve(user);
    }, reject);
  });
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const user = await getCurrentUser();


  // Custom redirect for root "/" (no longer needed, handled by route above)

  if (requiresAuth && !user) {
    return next('/login');
  }

  if (requiresAdmin) {
    let profile = store.state.profile;

    // Store may not be populated yet (e.g. a hard refresh straight into an admin route)
    if (!profile) {
      profile = await getUserProfile(user.uid);
      if (profile) store.commit('setProfile', profile);
    }

    if (!profile || profile.role !== 'admin') {
      return next('/profile');
    }
  }

  next();
});

// Dynamic title
router.afterEach((to) => {
  const defaultTitle = 'Success Boost';
  document.title = (to.meta && to.meta.title) ? to.meta.title : defaultTitle;
});

export default router;
